<template>
  <div>
    <NavbarTwo />
    <div class="static-hero">
      <div class="container mw-1356">
        <span class="top-title">CHANNELS</span>
        <h1>Our Channel Lineup</h1>
        <p>Browse our full library of live TV channels</p>
      </div>
    </div>

    <div class="static-body bg-gondola">
      <div class="container mw-1356">

        <!-- Search + category filters -->
        <div class="ch-toolbar">
          <input
            v-model="search"
            type="text"
            class="ch-search"
            placeholder="Search channels..."
          />
          <div class="ch-cats">
            <button
              v-for="cat in categories"
              :key="cat"
              class="ch-cat-btn"
              :class="{ active: activeCategory === cat }"
              @click="activeCategory = cat"
            >
              {{ cat }}
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="ch-loading">
          <span class="ch-spinner"></span> Loading channels...
        </div>

        <!-- Empty -->
        <div v-else-if="!filtered.length" class="ch-empty">
          <i class="ti ti-device-tv-off"></i>
          <p>No channels found{{ search ? ' for "' + search + '"' : '' }}.</p>
        </div>

        <!-- Grid -->
        <div v-else class="ch-grid">
          <div v-for="ch in filtered" :key="ch.id" class="ch-card">
            <div class="ch-logo-wrap">
              <img v-if="ch.logo_url" :src="ch.logo_url" :alt="ch.name" class="ch-logo" />
              <div v-else class="ch-logo-placeholder">
                <i class="ti ti-device-tv"></i>
              </div>
            </div>
            <div class="ch-info">
              <div class="ch-name">{{ ch.name }}</div>
              <div class="ch-meta">
                <span v-if="ch.category" class="ch-badge">{{ ch.category }}</span>
                <span v-if="ch.country" class="ch-country">{{ ch.country }}</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <MainFooter />
    <CopyRight />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useSeo } from "@/composables/useSeo";
import NavbarTwo from "@/components/Layouts/NavbarTwo.vue";
import MainFooter from "@/components/Layouts/MainFooter.vue";
import CopyRight from "@/components/Layouts/CopyRight.vue";
import api from "@/services/api";

interface Channel {
  id: number;
  name: string;
  logo_url: string | null;
  category: string;
  country: string | null;
  is_active: boolean;
  sort_order: number;
}

export default defineComponent({
  name: "ChannelsPage",
  components: { NavbarTwo, MainFooter, CopyRight },
  setup() {
    useSeo({
      title: "Full Channel List — 25,000+ Live Channels",
      description: "Browse goplayTv's full channel list. Sports, news, movies, entertainment, kids, and World Cup 2026 channels in HD and 4K. All countries covered.",
      url: "/channels",
      keywords: "IPTV channel list, live sports channels, World Cup 2026 channels, HD TV channels, IPTV channel guide",
    });
    const channels = ref<Channel[]>([]);
    const loading = ref(true);
    const search = ref("");
    const activeCategory = ref("All");

    onMounted(async () => {
      try {
        const { data } = await api.get("/api/v1/channels");
        channels.value = data.data ?? data;
      } catch {
        // show empty state
      } finally {
        loading.value = false;
      }
    });

    const categories = computed(() => {
      const cats = new Set(channels.value.map((c) => c.category).filter(Boolean));
      return ["All", ...Array.from(cats).sort()];
    });

    const filtered = computed(() => {
      const q = search.value.toLowerCase().trim();
      return channels.value.filter((c) => {
        const matchCat = activeCategory.value === "All" || c.category === activeCategory.value;
        const matchSearch = !q || c.name.toLowerCase().includes(q) || (c.category || "").toLowerCase().includes(q);
        return matchCat && matchSearch;
      });
    });

    return { channels, loading, search, activeCategory, categories, filtered };
  },
});
</script>

<style scoped>
.static-hero {
  background: linear-gradient(135deg, #0a0102 0%, #1a0508 100%);
  padding: 100px 0 60px;
  border-bottom: 1px solid #2b1e1f;
  text-align: center;
}
.static-hero .top-title { display: inline-block; color: #c90e0e; font-size: 0.8rem; font-weight: 700; letter-spacing: 3px; text-transform: uppercase; margin-bottom: 1rem; }
.static-hero h1 { color: #fff; font-size: clamp(1.8rem, 4vw, 2.6rem); font-weight: 800; margin-bottom: 0.75rem; }
.static-hero p { color: #a0a0a0; }

.static-body { padding: 50px 0 80px; }

.ch-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-bottom: 2rem;
}
.ch-search {
  flex: 1;
  min-width: 200px;
  max-width: 340px;
  background: #1a0f10;
  border: 1px solid #3a2526;
  border-radius: 8px;
  color: #fff;
  padding: 0.55rem 1rem;
  font-size: 0.9rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s;
}
.ch-search:focus { border-color: #c90e0e; }
.ch-search::placeholder { color: #555; }

.ch-cats { display: flex; flex-wrap: wrap; gap: 0.4rem; }
.ch-cat-btn {
  background: #1a0f10;
  border: 1px solid #3a2526;
  color: #a0a0a0;
  border-radius: 99px;
  padding: 0.3rem 0.9rem;
  font-size: 0.8rem;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s;
}
.ch-cat-btn:hover { border-color: #c90e0e; color: #fff; }
.ch-cat-btn.active { background: #c90e0e; border-color: #c90e0e; color: #fff; }

.ch-loading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #a0a0a0;
  padding: 3rem 0;
  justify-content: center;
}
.ch-spinner {
  width: 20px; height: 20px;
  border: 2px solid #3a2526;
  border-top-color: #c90e0e;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes spin { to { transform: rotate(360deg); } }

.ch-empty {
  text-align: center;
  padding: 4rem 0;
  color: #555;
}
.ch-empty i { font-size: 3rem; display: block; margin-bottom: 0.75rem; }
.ch-empty p { font-size: 0.95rem; }

.ch-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
}

.ch-card {
  background: #1a0f10;
  border: 1px solid #2b1e1f;
  border-radius: 10px;
  overflow: hidden;
  transition: border-color 0.2s, transform 0.2s;
  cursor: default;
}
.ch-card:hover { border-color: #c90e0e; transform: translateY(-2px); }

.ch-logo-wrap {
  aspect-ratio: 16 / 9;
  background: #0a0102;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.ch-logo { width: 100%; height: 100%; object-fit: contain; padding: 0.5rem; }
.ch-logo-placeholder { color: #3a2526; font-size: 2rem; }

.ch-info { padding: 0.6rem 0.75rem 0.75rem; }
.ch-name { color: #fff; font-size: 0.82rem; font-weight: 600; margin-bottom: 0.3rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ch-meta { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; }
.ch-badge { background: rgba(201,14,14,0.15); color: #c90e0e; font-size: 0.68rem; padding: 0.1rem 0.45rem; border-radius: 4px; }
.ch-country { color: #555; font-size: 0.7rem; }
</style>
